/**
 * Shopify Storefront API client — minimal, dependency-free.
 * Uses native fetch + GraphQL against the public Storefront endpoint.
 *
 * All credentials come from /app/frontend/.env:
 *   REACT_APP_SHOPIFY_DOMAIN
 *   REACT_APP_SHOPIFY_STOREFRONT_TOKEN
 *   REACT_APP_SHOPIFY_API_VERSION
 */

const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || "2025-01";

export const SHOPIFY_CONFIGURED = Boolean(DOMAIN && TOKEN);

const endpoint = DOMAIN
  ? `https://${DOMAIN}/api/${VERSION}/graphql.json`
  : null;

/**
 * Shopify returns checkoutUrl using the store's primary domain.
 * In our headless setup, the primary domain (girlygrove.in) actually
 * serves the Emergent frontend, so the checkout URL would 404.
 * Force checkout onto the *.myshopify.com domain so payments always work,
 * regardless of which custom domain is primary.
 */
const normalizeCheckoutUrl = (url) => {
  if (!url || !DOMAIN) return url;
  try {
    const u = new URL(url);
    u.host = DOMAIN; // e.g. 6btdtv-cr.myshopify.com
    return u.toString();
  } catch {
    return url;
  }
};

/**
 * Low-level GraphQL caller.
 * Throws on network errors or GraphQL errors.
 */
export async function shopifyFetch(query, variables = {}) {
  if (!SHOPIFY_CONFIGURED) {
    throw new Error("Shopify is not configured. Check .env credentials.");
  }

  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": TOKEN,
      Accept: "application/json",
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Shopify HTTP ${res.status}: ${text.slice(0, 200)}`);
  }

  const json = await res.json();
  if (json.errors?.length) {
    throw new Error(
      "Shopify GraphQL error: " +
        json.errors.map((e) => e.message).join(", "),
    );
  }
  return json.data;
}

/* ------------------------------------------------------------------ */
/*  Mappers — Shopify shape → GirlyGrove product shape used by UI     */
/* ------------------------------------------------------------------ */

const toCents = (s) => Math.round(parseFloat(s || "0"));

/** Decode common HTML entities that can appear in Shopify titles (e.g. `&amp;`). */
const decodeEntities = (str = "") => {
  if (typeof document === "undefined") return str;
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
};

/**
 * Map a Shopify product node to the same shape our existing UI uses:
 *   { id, slug, name, price, mrp, image, images, category, tags, badge,
 *     variantId, available, description, descriptionHtml }
 */
export function mapShopifyProduct(node) {
  if (!node) return null;

  const variant = node.variants?.edges?.[0]?.node;
  const price = variant?.price?.amount ?? node.priceRange?.minVariantPrice?.amount;
  const compareAt =
    variant?.compareAtPrice?.amount ??
    node.compareAtPriceRange?.minVariantPrice?.amount;
  const priceNum = toCents(price);
  const compareAtNum = compareAt ? toCents(compareAt) : 0;
  // Only treat compare-at as a "real" MRP when it's strictly above the sale price.
  const mrp = compareAtNum > priceNum ? compareAtNum : null;

  const images = (node.images?.edges || []).map((e) => e.node.url);

  const tags = node.tags || [];
  // First product type / tag becomes the category slug for filtering
  const category =
    (node.productType && node.productType.toLowerCase().replace(/\s+/g, "-")) ||
    tags[0]?.toLowerCase().replace(/\s+/g, "-") ||
    "all";

  // Light badge logic from tags
  let badge = null;
  const tagLower = tags.map((t) => t.toLowerCase());
  if (tagLower.includes("new")) badge = "New";
  else if (tagLower.includes("bestseller")) badge = "Bestseller";
  else if (tagLower.includes("hot")) badge = "Hot";
  else if (tagLower.includes("combo") || tagLower.includes("set")) badge = "Set";
  else if (mrp) badge = "Selling Fast";

  return {
    id: node.handle, // use handle as our public id for clean URLs
    shopifyId: node.id,
    slug: node.handle,
    name: decodeEntities(node.title),
    description: decodeEntities(node.description || ""),
    descriptionHtml: node.descriptionHtml || "",
    price: priceNum,
    mrp,
    image: images[0] || null,
    images,
    category,
    tags,
    badge,
    available: node.availableForSale,
    variantId: variant?.id || null,
  };
}

/* ------------------------------------------------------------------ */
/*  Queries                                                            */
/* ------------------------------------------------------------------ */

const PRODUCT_FIELDS = `
  id
  handle
  title
  description
  descriptionHtml
  productType
  tags
  availableForSale
  priceRange {
    minVariantPrice { amount currencyCode }
  }
  compareAtPriceRange {
    minVariantPrice { amount currencyCode }
  }
  images(first: 8) {
    edges {
      node {
        url(transform: {maxWidth: 1200, maxHeight: 1500, crop: CENTER, preferredContentType: WEBP})
        altText
      }
    }
  }
  variants(first: 1) {
    edges {
      node {
        id
        availableForSale
        price { amount currencyCode }
        compareAtPrice { amount currencyCode }
      }
    }
  }
`;

export async function fetchProducts({ first = 24, query = "" } = {}) {
  const data = await shopifyFetch(
    `query Products($first: Int!, $query: String) {
       products(first: $first, query: $query, sortKey: BEST_SELLING) {
         edges { node { ${PRODUCT_FIELDS} } }
       }
     }`,
    { first, query: query || null },
  );
  return data.products.edges.map((e) => mapShopifyProduct(e.node));
}

export async function fetchProductByHandle(handle) {
  const data = await shopifyFetch(
    `query Product($handle: String!) {
       product(handle: $handle) {
         ${PRODUCT_FIELDS}
         collections(first: 20) { edges { node { handle } } }
       }
     }`,
    { handle },
  );
  const mapped = mapShopifyProduct(data.product);
  if (mapped) mapped.collectionHandles = data.product.collections.edges.map((e) => e.node.handle);
  return mapped;
}

export async function fetchCollections({ first = 20 } = {}) {
  const data = await shopifyFetch(
    `query Collections($first: Int!) {
       collections(first: $first) {
         edges {
           node {
             id
             handle
             title
             description
             image { url altText }
           }
         }
       }
     }`,
    { first },
  );
  return data.collections.edges.map((e) => ({
    id: e.node.handle,
    name: e.node.title,
    description: e.node.description,
    image: e.node.image?.url || null,
  }));
}

export async function fetchProductsByCollection(handle, { first = 24 } = {}) {
  const data = await shopifyFetch(
    `query CollectionProducts($handle: String!, $first: Int!) {
       collection(handle: $handle) {
         id
         handle
         title
         products(first: $first) {
           edges { node { ${PRODUCT_FIELDS} } }
         }
       }
     }`,
    { handle, first },
  );
  if (!data.collection) return [];
  return data.collection.products.edges.map((e) => mapShopifyProduct(e.node));
}

/* ------------------------------------------------------------------ */
/*  Cart (Storefront Cart API — replaces deprecated Checkout API)      */
/* ------------------------------------------------------------------ */

const CART_FIELDS = `
  id
  checkoutUrl
  totalQuantity
  cost {
    subtotalAmount { amount currencyCode }
    totalAmount    { amount currencyCode }
  }
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            price { amount currencyCode }
            product {
              handle
              title
              images(first: 1) { edges { node { url } } }
            }
          }
        }
      }
    }
  }
`;

export async function cartCreate(lines = []) {
  const data = await shopifyFetch(
    `mutation CartCreate($input: CartInput!) {
       cartCreate(input: $input) {
         cart { ${CART_FIELDS} }
         userErrors { field message }
       }
     }`,
    { input: { lines } },
  );
  const cart = data.cartCreate.cart;
  if (cart) cart.checkoutUrl = normalizeCheckoutUrl(cart.checkoutUrl);
  return cart;
}

export async function cartLinesAdd(cartId, lines) {
  const data = await shopifyFetch(
    `mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
       cartLinesAdd(cartId: $cartId, lines: $lines) {
         cart { ${CART_FIELDS} }
         userErrors { field message }
       }
     }`,
    { cartId, lines },
  );
  const cart = data.cartLinesAdd.cart;
  if (cart) cart.checkoutUrl = normalizeCheckoutUrl(cart.checkoutUrl);
  return cart;
}

export async function cartLinesUpdate(cartId, lines) {
  const data = await shopifyFetch(
    `mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
       cartLinesUpdate(cartId: $cartId, lines: $lines) {
         cart { ${CART_FIELDS} }
         userErrors { field message }
       }
     }`,
    { cartId, lines },
  );
  const cart = data.cartLinesUpdate.cart;
  if (cart) cart.checkoutUrl = normalizeCheckoutUrl(cart.checkoutUrl);
  return cart;
}

export async function cartLinesRemove(cartId, lineIds) {
  const data = await shopifyFetch(
    `mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
       cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
         cart { ${CART_FIELDS} }
         userErrors { field message }
       }
     }`,
    { cartId, lineIds },
  );
  const cart = data.cartLinesRemove.cart;
  if (cart) cart.checkoutUrl = normalizeCheckoutUrl(cart.checkoutUrl);
  return cart;
}
